import { createElement, useEffect, useState } from 'react';
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Globe, Play, Pause, Trash2, ArrowRightLeft } from 'lucide-react';
import {
  Breadcrumbs,
  BreadcrumbItem,
  Container,
  PageHeader,
  type ContextualNavEntry,
  type ContextualNavSection,
} from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useDomainDetail,
  useUpdateDomain,
  useDeleteDomain,
} from '@/api/manticore';
import { useCdnUmbrellas } from '@/api/manticore/cdn-providers';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { domainStatusTone, domainStatusLabel, isDomainDeletable, UUID_RE } from './shared';
import { DomainMoveDialog } from './DomainMoveDialog';
import { DOMAIN_NAV, domainHref, domainActiveId } from './domain/nav';
import { useWafEnabled } from '@/api/orgs';
import { TabStub } from './domain/TabStub';
import { DomainOverviewTab } from './domain/OverviewTab';
import { CacheTab } from './domain/CacheTab';
import { SslTab } from './domain/SslTab';
import { OriginTab } from './domain/upstream/OriginTab';
import { OriginRewriteTab } from './domain/OriginRewriteTab';
import { HeaderRulesTab } from './domain/HeaderRulesTab';
import { ErrorPagesTab } from './domain/ErrorPagesTab';
import { RedirectsTab } from './domain/RedirectsTab';
import { SecurityHeadersTab } from './domain/security/SecurityHeadersTab';
import { VerifiedBotsTab } from './domain/security/VerifiedBotsTab';
import { BotManagementTab } from './domain/security/BotManagementTab';
import { CorsTab } from './domain/security/CorsTab';
import { RateLimitingTab } from './domain/security/RateLimitingTab';
import { AccessControlTab } from './domain/security/AccessControlTab';
import { WafTab } from './domain/security/WafTab';
import { FirewallRulesTab } from './domain/security/FirewallRulesTab';
import { WafAnalyticsTab } from './domain/security/WafAnalyticsTab';
import { BotMgmtAnalyticsTab } from './domain/security/BotMgmtAnalyticsTab';
import { PurgeWarmTab } from './domain/PurgeWarmTab';
import { OptimizationTab } from './domain/OptimizationTab';
import { OperationsTab } from './domain/OperationsTab';

const betaBadge = (
  <Badge tone="info" appearance="subtle" size="sm">
    beta
  </Badge>
);

export function DomainDetail() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { domainId, serviceId: scopedServiceId } = useParams<{ domainId: string; serviceId?: string }>();
  const [searchParams] = useSearchParams();
  const { show } = useToast();
  const [confirm, setConfirm] = useState<'toggle' | 'delete' | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);

  // WAF surfaces (WAF + WAF Analytics) are hidden unless the account has WAF
  // entitlement (org.waf_enabled from GET /orgs). Mirrors the legacy partner
  // console's gate.
  const wafEnabled = useWafEnabled();
  // The API doesn't expose a domain's owning service yet, so the orchestrator
  // console stamps itself via ?from=<serviceId> when linking here. With it we
  // can render the owning breadcrumb and send "back" to that console's domains
  // page instead of the org-wide orchestrator list.
  // Owning service: from the path when the console mounts us nested
  // (/services/cdn/:serviceId/domains/:domainId), else stamped via ?from= by the
  // console (the API doesn't expose a domain's owning service yet). The nested
  // mount keeps the id in the path, so tab hops don't need ?from=; the legacy
  // top-level mount keeps stamping it.
  const fromParam = searchParams.get('from');
  const fromQuery = fromParam && UUID_RE.test(fromParam) ? fromParam : undefined;
  const fromServiceId = scopedServiceId ?? fromQuery;
  const fromQS = !scopedServiceId && fromServiceId ? `?from=${fromServiceId}` : '';
  const { data: umbrellasData } = useCdnUmbrellas(undefined, { enabled: !!fromServiceId });
  const fromUmbrella = fromServiceId
    ? umbrellasData?.umbrellas.find((u) => u.service_id === fromServiceId)
    : undefined;
  const backTarget = fromServiceId ? `/services/cdn/${fromServiceId}/domains` : '/services/cdn';

  const base = scopedServiceId
    ? `/services/cdn/${scopedServiceId}/domains/${domainId}`
    : `/services/domains/${domainId}`;
  useSetContextualNav(() => {
    const sections: ContextualNavSection[] = [
      {
        items: DOMAIN_NAV.map((entry): ContextualNavEntry => {
          const icon = createElement(entry.icon, { size: 16 });
          if (entry.items?.length) {
            const leaves = entry.items.filter(
              (leaf) =>
                wafEnabled ||
                (leaf.slug !== 'security/waf' && leaf.slug !== 'security/waf-analytics'),
            );
            return {
              id: entry.label,
              label: entry.label,
              icon,
              items: leaves.map((leaf) => ({
                id: domainHref(base, leaf.slug),
                label: leaf.label,
                badge: leaf.beta ? betaBadge : undefined,
                // Keep ?from= while hopping tabs so the owning-console context survives.
                onSelect: () => navigate(`${domainHref(base, leaf.slug)}${fromQS}`),
              })),
            };
          }
          const href = domainHref(base, entry.slug ?? '');
          return {
            id: href,
            label: entry.label,
            icon,
            badge: entry.beta ? betaBadge : undefined,
            onSelect: () => navigate(`${href}${fromQS}`),
          };
        }),
      },
    ];
    return {
      sections,
      activeId: domainActiveId(base, pathname),
      back: {
        label: fromServiceId ? 'Back to Domains' : 'Back to CDN',
        onSelect: () => navigate(backTarget),
      },
    };
  }, [base, pathname, navigate, fromQS, fromServiceId, backTarget, wafEnabled]);

  // FE only ever sends UUIDs to the BE — bounce legacy FQDN bookmarks to list.
  const isValidId = !!domainId && UUID_RE.test(domainId);
  useEffect(() => {
    if (domainId && !isValidId) navigate('/services/cdn', { replace: true });
  }, [domainId, isValidId, navigate]);

  const { data: domain, isLoading } = useDomainDetail(isValidId ? domainId : undefined);

  const status = domain?.status as string | undefined;
  const isEnabled = status === 'active' || status === 'deploying';
  const name = domain?.domain ?? domainId ?? '';

  const update = useUpdateDomain({
    onSuccess: () => {
      show({ tone: 'success', title: `Domain ${isEnabled ? 'disabled' : 'enabled'}` });
      setConfirm(null);
    },
    onError: () => show({ tone: 'danger', title: 'Update failed' }),
  });
  const remove = useDeleteDomain({
    onSuccess: () => {
      show({ tone: 'success', title: 'Domain deleted' });
      navigate(backTarget);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  if (!isValidId) return null;

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 36, width: 280, marginBottom: 'var(--vn-space-6)' }} />
        <Skeleton style={{ height: 320 }} />
      </Container>
    );
  }

  if (!domain) {
    return (
      <Container size="xl">
        <EmptyState
          icon={<Globe size={28} />}
          title="Domain not found"
          description="It may have been deleted, or you don't have access to it."
          actions={
            <Button variant="secondary" onClick={() => navigate('/services/cdn')}>
              Back to CDN
            </Button>
          }
        />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Globe size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            {fromServiceId
              ? // Flat array (NOT a Fragment): Breadcrumbs adds a separator between
                // each direct child, and a Fragment would collapse both hops into
                // one child — gluing them ("… 15564Domains").
                [
                  <BreadcrumbItem key="service" asChild>
                    <Link to={`/services/cdn/${fromServiceId}`}>
                      {fromUmbrella?.service_name ?? 'Multi-CDN Orchestration'}
                    </Link>
                  </BreadcrumbItem>,
                  <BreadcrumbItem key="domains" asChild>
                    <Link to={backTarget}>Domains</Link>
                  </BreadcrumbItem>,
                ]
              : (
                  <BreadcrumbItem asChild>
                    <Link to="/services/cdn">Multi-CDN Orchestration</Link>
                  </BreadcrumbItem>
                )}
          </Breadcrumbs>
        }
        title={name}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <StatusPill tone={domainStatusTone(status)}>{domainStatusLabel(status)}</StatusPill>
            <Button
              variant="secondary"
              startIcon={<ArrowRightLeft size={15} />}
              onClick={() => setMoveOpen(true)}
            >
              Move to service
            </Button>
            <Button
              variant="secondary"
              startIcon={isEnabled ? <Pause size={15} /> : <Play size={15} />}
              disabled={update.isPending}
              onClick={() => setConfirm('toggle')}
            >
              {isEnabled ? 'Disable' : 'Enable'}
            </Button>
            <Button
              variant="danger"
              startIcon={<Trash2 size={15} />}
              disabled={!isDomainDeletable(status) || remove.isPending}
              onClick={() => setConfirm('delete')}
            >
              Delete
            </Button>
          </div>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {status === 'suspended' && (
        <Alert tone="danger">
          Domain <code>{name}</code> is <b>suspended</b>. Traffic is not served from the edge until you re-enable it.
        </Alert>
      )}
      {status === 'pending' && (
        <Alert tone="info">
          Domain <code>{name}</code> is being prepared. DNS verification and edge propagation may take a few minutes.
        </Alert>
      )}
      {status === 'deploying' && (
        <Alert tone="warning">
          Configuration changes for <code>{name}</code> are deploying across the edge. Some settings may not be active yet.
        </Alert>
      )}

      <Routes>
        <Route index element={<DomainOverviewTab domain={domain} />} />
        <Route path="cache" element={<CacheTab domain={name} domainId={domainId!} />} />
        <Route path="ssl" element={<SslTab domain={name} domainId={domainId!} />} />
        <Route path="upstream" element={<OriginTab domain={name} domainId={domainId!} />} />
        <Route path="origin-rewrite" element={<OriginRewriteTab domain={name} domainId={domainId!} />} />
        <Route path="header-rules" element={<HeaderRulesTab domain={name} domainId={domainId!} />} />
        <Route path="error-pages" element={<ErrorPagesTab domain={name} domainId={domainId!} />} />
        <Route path="redirects" element={<RedirectsTab domain={name} domainId={domainId!} />} />
        <Route path="purge" element={<PurgeWarmTab domain={name} domainId={domainId!} />} />
        <Route path="optimization" element={<OptimizationTab domain={name} domainId={domainId!} />} />
        <Route path="operations" element={<OperationsTab domain={name} domainId={domainId!} />} />
        {/* Security sub-pages */}
        <Route path="security/waf" element={<WafTab domain={name} domainId={domainId!} />} />
        <Route path="security/firewall-rules" element={<FirewallRulesTab domain={name} domainId={domainId!} />} />
        <Route path="security/waf-analytics" element={<WafAnalyticsTab domain={name} domainId={domainId!} />} />
        <Route path="security/rate-limiting" element={<RateLimitingTab domain={name} domainId={domainId!} />} />
        <Route path="security/access-control" element={<AccessControlTab domain={name} domainId={domainId!} />} />
        <Route path="security/cors" element={<CorsTab domain={name} domainId={domainId!} />} />
        <Route path="security/security-headers" element={<SecurityHeadersTab domain={name} domainId={domainId!} />} />
        <Route path="security/verified-bots" element={<VerifiedBotsTab domain={name} domainId={domainId!} />} />
        <Route path="security/bot-management" element={<BotManagementTab domain={name} domainId={domainId!} />} />
        <Route path="security/bot-management-analytics" element={<BotMgmtAnalyticsTab domain={name} domainId={domainId!} />} />
        <Route path="security/*" element={<SecurityStub name={name} />} />
        <Route
          path="*"
          element={<TabStub title="Overview" description={`Configuration for ${name}.`} />}
        />
      </Routes>
      </div>

      <ConfirmDialog
        open={confirm === 'toggle'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={isEnabled ? 'Disable CDN domain?' : 'Enable CDN domain?'}
        description={
          isEnabled
            ? `Disabling “${name}” stops edge traffic until you re-enable it.`
            : `Re-enabling “${name}” resumes edge traffic.`
        }
        confirmLabel={isEnabled ? 'Disable' : 'Enable'}
        destructive={isEnabled}
        requireText={isEnabled ? name : undefined}
        loading={update.isPending}
        onConfirm={() => update.mutate({ domain: domainId!, enabled: !isEnabled })}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Delete CDN domain?"
        description={`This permanently removes all configuration for “${name}”. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={name}
        loading={remove.isPending}
        onConfirm={() => remove.mutate(domainId!)}
      />
      {/* Re-point this domain to another same-provider service. On success the
          dialog lands on the destination service's domain list. */}
      <DomainMoveDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        domain={domain}
        onDone={() => setMoveOpen(false)}
      />
    </Container>
  );
}


function SecurityStub({ name }: { name: string }) {
  const params = useParams();
  const sub = params['*'] || 'waf';
  const titles: Record<string, string> = {
    waf: 'WAF',
    'firewall-rules': 'Firewall Rules',
    'waf-analytics': 'WAF Analytics',
    'rate-limiting': 'Rate Limiting',
    'access-control': 'Access Control',
    cors: 'CORS',
    'security-headers': 'Security Headers',
    'bot-management': 'Bot Management',
    'bot-management-analytics': 'Bot Management Analytics',
  };
  const title = titles[sub] ?? 'Security';
  return <TabStub title={title} description={`${title} configuration for ${name}.`} />;
}
